import { useCallback, useEffect, useMemo } from 'react';

import type { Dayjs } from 'dayjs';
import { useQueryParams } from 'use-query-params';

import type { DateRange } from '@packages/fenrir/components';
import { DayJsParam } from '@packages/utils/dayjs.utils';

export type DateRangeQueryParams = {
  dateRange: DateRange<Dayjs>;
};

export const useDateRangeQueryParams = (
  defaultDateRange: DateRange<Dayjs>,
  startQueryParamName = 'dateStart',
  endQueryParamName = 'dateEnd',
): [DateRangeQueryParams, (queryParams: DateRangeQueryParams) => void] => {
  const [queryParams, setQueryParams] = useQueryParams({
    [startQueryParamName]: DayJsParam,
    [endQueryParamName]: DayJsParam,
  });

  useEffect(() => {
    if (
      queryParams[startQueryParamName] !== null &&
      queryParams[endQueryParamName] !== null
    ) {
      return;
    }

    const [dateStart, dateEnd] = defaultDateRange;

    setQueryParams({
      [startQueryParamName]: dateStart,
      [endQueryParamName]: dateEnd,
    });
  }, [
    defaultDateRange,
    endQueryParamName,
    queryParams,
    setQueryParams,
    startQueryParamName,
  ]);

  const dateRangeQueryParams = useMemo<DateRangeQueryParams>(() => {
    return {
      dateRange:
        queryParams[startQueryParamName]?.isValid?.() &&
        queryParams[endQueryParamName]?.isValid?.()
          ? [queryParams[startQueryParamName], queryParams[endQueryParamName]]
          : defaultDateRange,
    };
  }, [defaultDateRange, endQueryParamName, queryParams, startQueryParamName]);

  const setDateRangeQueryParams = useCallback(
    ({ dateRange: [dateStart, dateEnd] }: DateRangeQueryParams) => {
      setQueryParams({
        [startQueryParamName]: dateStart,
        [endQueryParamName]: dateEnd,
      });
    },
    [endQueryParamName, setQueryParams, startQueryParamName],
  );

  return [dateRangeQueryParams, setDateRangeQueryParams];
};
